/**
 * ExtractedFieldsCard — displays OCR / provider-extracted identity fields
 * in an inline-editable card. Tap any field to edit; unreadable fields
 * are highlighted with a warning color and "needs attention" affordance.
 *
 * Used in the photo-first verification flow after the edge function
 * returns extracted data.
 */
import { useState, useCallback } from 'react'
import { View, Text } from 'react-native'
import { useTranslation } from 'react-i18next'
import Animated, { FadeInDown, useReducedMotion } from 'react-native-reanimated'
import {
  IsoDateInput,
  PersonNameInput,
  StructuredTextInput,
  SvDuiInput,
} from '@/components/ui/StructuredTextInput'
import { DatePicker } from '@/components/ui/DatePicker'
import { useSemanticColors, SPACING, RADIUS, DURATION } from '@/lib/theme/tokens'
import { FONTS } from '@/lib/brand'
import { useTheme } from '@/lib/hooks/use-theme'
import { WEB_SHADOWS } from '@/lib/theme/web-shadow'
import { yearsAgoISO } from '@/lib/utils/dates'
import type { ExtractedIdentityData, IdDocumentType } from '@/lib/types/verification.types'

type EditableField =
  | 'firstName'
  | 'lastName'
  | 'dateOfBirth'
  | 'documentNumber'
  | 'issueDate'
  | 'expiryDate'

interface ExtractedFieldsCardProps {
  data: ExtractedIdentityData
  onChange: (field: keyof ExtractedIdentityData, value: string) => void
  /** Fields to display; defaults to the core 4 (name, DOB, number). */
  fields?: EditableField[]
  documentType?: IdDocumentType
}

const DEFAULT_FIELDS: EditableField[] = ['firstName', 'lastName', 'dateOfBirth', 'documentNumber']

export function ExtractedFieldsCard({
  data,
  onChange,
  fields = DEFAULT_FIELDS,
  documentType,
}: ExtractedFieldsCardProps) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const reducedMotion = useReducedMotion()

  return (
    <Animated.View
      entering={reducedMotion ? undefined : FadeInDown.duration(DURATION.normal)}
      collapsable={false}
      style={{
        backgroundColor: colors.surface,
        borderRadius: RADIUS.lg,
        padding: SPACING.md,
        gap: SPACING.md,
        ...WEB_SHADOWS.md,
      }}
    >
      <Text
        style={{
          fontSize: 12,
          fontFamily: FONTS.body,
          color: colors.textMuted,
          textTransform: 'uppercase',
          letterSpacing: 0.8,
        }}
      >
        {t('verification.extracted_fields_title')}
      </Text>

      {fields.map((f) => (
        <EditableRow
          key={f}
          field={f}
          value={toDisplay(data, f)}
          documentType={documentType}
          onCommit={(v) => onChange(f, v)}
          label={t(`verification.field_${f}`)}
          placeholder={t(`verification.field_${f}_placeholder`)}
        />
      ))}
    </Animated.View>
  )
}

interface EditableRowProps {
  field: EditableField
  value: string
  label: string
  placeholder: string
  documentType?: IdDocumentType
  onCommit: (value: string) => void
}

function EditableRow({
  field,
  value,
  label,
  placeholder,
  documentType,
  onCommit,
}: EditableRowProps) {
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const [local, setLocal] = useState(value)
  const [touched, setTouched] = useState(false)

  // Keep local in sync if the parent re-fetches OCR results
  if (!touched && local !== value) {
    setLocal(value)
  }

  const handleBlur = useCallback(() => {
    if (local !== value) onCommit(local)
  }, [local, value, onCommit])

  const handleChange = useCallback(
    (text: string) => {
      setLocal(text)
      setTouched(true)
      onCommit(text)
    },
    [onCommit],
  )

  const handleDateChange = useCallback(
    (date: string) => {
      setLocal(date)
      setTouched(true)
      onCommit(date)
    },
    [onCommit],
  )

  const isEmpty = !local || local.trim().length === 0
  const isBirthDate = field === 'dateOfBirth'
  const isDateField = field === 'issueDate' || field === 'expiryDate'
  const isDuiNumber = field === 'documentNumber' && documentType === 'dui'

  if (isBirthDate) {
    return (
      <View style={{ gap: SPACING.xs }}>
        <Text
          style={{
            fontSize: 12,
            fontFamily: FONTS.body,
            color: isEmpty ? colors.warning : colors.textSecondary,
          }}
        >
          {label}
          {isEmpty ? ' · ⚠︎' : ''}
        </Text>
        <DatePicker
          value={local || null}
          onChange={handleDateChange}
          minDate={yearsAgoISO(100)}
          maxDate={yearsAgoISO(18)}
          testID={`verification.field-${field}`}
        />
      </View>
    )
  }

  const Input =
    field === 'firstName' || field === 'lastName'
      ? PersonNameInput
      : isDateField
        ? IsoDateInput
        : isDuiNumber
          ? SvDuiInput
          : StructuredTextInput

  return (
    <View style={{ gap: SPACING.xs }}>
      <Text
        style={{
          fontSize: 12,
          fontFamily: FONTS.body,
          color: isEmpty ? colors.warning : colors.textSecondary,
        }}
      >
        {label}
        {isEmpty ? ' · ⚠︎' : ''}
      </Text>
      <Input
        value={local}
        onChangeText={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        accessibilityLabel={label}
        testID={`verification.field-${field}`}
      />
    </View>
  )
}

function toDisplay(data: ExtractedIdentityData, field: EditableField): string {
  const raw = data[field]
  if (raw == null) return ''
  return String(raw)
}
